import * as FullStory from '@fullstory/browser';
import React, {
	createContext,
	FC,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useSelector } from 'react-redux';

import {
	getRemainingMessages,
	getTodaysMessageCount,
	hasReachedLimit,
} from '~components/business-owner/barry/utils';

import { useGetConversationsForUser } from '~api/common/barry/queries/useGetConversationsForUser';
import { FULL_STORY_BARRY_TRACKING_EVENTS } from '~constants/fullstory/events/barry';
import { GlobalState } from '~reducers';

export enum UIModeEnum {
	CONVERSATION = 'conversation',
	NEW = 'new',
	TRANSITION = 'transition',
}
export type UIMode = UIModeEnum;

interface BarryContextType {
	openBarry: () => void;
	closeBarry: () => void;
	toggleBarry: () => void;
	isBarryOpen: boolean;
	selectedConversationId: string | null;
	setSelectedConversationId: (id: string | null) => void;
	openWithConversation: (conversationId: string) => void;
	uiMode: UIMode;
	startNewChat: () => void; // show welcome / new-chat screen
	beginTransition: () => void;
	completeNewChat: (conversationId: string) => void; // new convo got created
	todaysMessageCount: number;
	hasHitDailyLimit: boolean;
	remainingMessages: number;
	incrementMessageCount: () => void;
}

const BarryContext = createContext<BarryContextType | undefined>(undefined);

export const useBarry = () => {
	const context = useContext(BarryContext);

	if (!context) {
		throw new Error('useBarry must be used within a BarryProvider');
	}

	return context;
};

export const BarryProvider: FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const [isBarryOpen, setIsBarryOpen] = useState(false);
	const [selectedConversationId, setSelectedConversationId] = useState<string | null>(
		null,
	);

	const [uiMode, setUiMode] = useState<UIMode>(UIModeEnum.CONVERSATION);
	const [todaysMessageCount, setTodaysMessageCount] = useState(0);
	const [hasHitDailyLimit, setHasHitDailyLimit] = useState(false);
	const [remainingMessages, setRemainingMessages] = useState(0);

	const { userId, email, business, isLoggedIn } = useSelector(
		(state: GlobalState) => state?.session,
	);

	const { data: conversationsData } = useGetConversationsForUser(
		isLoggedIn && isBarryOpen,
	);

	useEffect(() => {
		if (!conversationsData) {
			return;
		}

		const count = getTodaysMessageCount(conversationsData);
		setTodaysMessageCount(count);
		setHasHitDailyLimit(hasReachedLimit(count));
		setRemainingMessages(getRemainingMessages(count));
	}, [conversationsData]);

	const incrementMessageCount = useCallback(() => {
		setTodaysMessageCount((prev) => {
			const newCount = prev + 1;
			setHasHitDailyLimit(hasReachedLimit(newCount));
			setRemainingMessages(getRemainingMessages(newCount));
			return newCount;
		});
	}, []);

	const openBarry = useCallback(() => setIsBarryOpen(true), []);
	const closeBarry = useCallback(() => setIsBarryOpen(false), []);
	const toggleBarry = useCallback(() => {
		setIsBarryOpen((prev) => !prev);
	}, []);

	useEffect(() => {
		if (isBarryOpen) {
			FullStory.event(FULL_STORY_BARRY_TRACKING_EVENTS.barryOpened, {
				userId,
				email,
				business,
			});
		}
	}, [isBarryOpen]);

	const startNewChat = useCallback((opts?: { open?: boolean }) => {
		setSelectedConversationId(null);
		setUiMode(UIModeEnum.NEW);
		if (opts?.open) {
			setIsBarryOpen(true);
		}
	}, []);

	const beginTransition = useCallback(() => {
		setUiMode(UIModeEnum.TRANSITION);
	}, []);

	const completeNewChat = useCallback((conversationId: string) => {
		setSelectedConversationId(conversationId);
		setUiMode(UIModeEnum.CONVERSATION);
	}, []);

	const openWithConversation = useCallback((conversationId: string) => {
		setSelectedConversationId(conversationId);
		setUiMode(UIModeEnum.CONVERSATION);
		setIsBarryOpen(true);
	}, []);

	const contextValue = useMemo(
		() => ({
			openBarry,
			closeBarry,
			toggleBarry,
			isBarryOpen,
			selectedConversationId,
			setSelectedConversationId,
			openWithConversation,
			uiMode,
			startNewChat,
			beginTransition,
			completeNewChat,
			todaysMessageCount,
			hasHitDailyLimit,
			remainingMessages,
			incrementMessageCount,
		}),
		[
			openBarry,
			closeBarry,
			toggleBarry,
			isBarryOpen,
			openWithConversation,
			setSelectedConversationId,
			selectedConversationId,
			uiMode,
			startNewChat,
			beginTransition,
			completeNewChat,
			todaysMessageCount,
			hasHitDailyLimit,
			remainingMessages,
			incrementMessageCount,
		],
	);

	return <BarryContext.Provider value={contextValue}>{children}</BarryContext.Provider>;
};
